import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";

// ============================================================================
// PAGEBIND — Photo Book PDF Composer
// Route: /books
//
// Upload many images -> choose how many land on each page (1-8) -> drag to
// adjust each photo with common, horizontal and vertical +/- zoom controls ->
// export the whole book as one downloadable, print-ready PDF.
//
// A solid blue center guide line (vertical, exactly at page center) can be
// toggled on/off via a checkbox (default: ON). When off, the line is not
// drawn in the preview OR baked into the exported PDF.
// ============================================================================

// ---- Printing methods and sheet sizes (mm) ---------------------------------
// Printing sheets for the photo-book composer. The same selected photo layout
// is retained in both modes; its physical slot sizes adapt to the sheet.
const SHEET_SIZE = { w: 460, h: 305, label: "460 × 305 mm" };
const ZOOM_STEP_MM = 0.5; // physical change per image zoom + / − click
const CANVAS_EXTENSION_STEP_MM = 0.5;
const MAX_CANVAS_EXTENSION_MM = 20;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;

const PRINT_SIZE_PRESETS = [
  { name: "A4", w: 210, h: 297 },
  { name: "A5", w: 148, h: 210 },
  { name: "A6", w: 105, h: 148 },
];
const SAVED_PRINT_SIZES_KEY = "pitics-offset-saved-print-sizes-v1";

function calculatePrintFit(sheetW, sheetH, itemW, itemH) {
  const normal = Math.floor(sheetW / itemW) * Math.floor(sheetH / itemH);
  const rotated = Math.floor(sheetW / itemH) * Math.floor(sheetH / itemW);
  return Math.max(normal, rotated);
}

function PrintSizeControls({ sheetW, sheetH, perPage, setPerPage, maxCount = 100 }) {
  const [manualName, setManualName] = useState("");
  const [manualW, setManualW] = useState("");
  const [manualH, setManualH] = useState("");
  const [savedSizes, setSavedSizes] = useState([]);
  const [activeSize, setActiveSize] = useState("");

  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(SAVED_PRINT_SIZES_KEY) || "[]");
      if (Array.isArray(parsed)) setSavedSizes(parsed.filter((x) =>
        x && typeof x.name === "string" && Number(x.w) > 0 && Number(x.h) > 0
      ));
    } catch (_) { setSavedSizes([]); }
  }, []);

  const applySize = (size) => {
    const fit = calculatePrintFit(sheetW, sheetH, Number(size.w), Number(size.h));
    if (!fit) {
      alert("This print size is larger than the current sheet. No complete item can fit.");
      return;
    }
    const count = Math.min(maxCount, fit);
    setPerPage(count);
    setActiveSize(size.name);
  };

  const saveManualSize = () => {
    const name = manualName.trim();
    const w = Number(manualW);
    const h = Number(manualH);
    if (!name || !Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
      alert("Enter a name and valid width and height in millimetres.");
      return;
    }
    const next = [...savedSizes.filter((x) => x.name.toLowerCase() !== name.toLowerCase()), { name, w, h }];
    setSavedSizes(next);
    try { localStorage.setItem(SAVED_PRINT_SIZES_KEY, JSON.stringify(next)); }
    catch (_) { alert("Could not save this print size in browser storage."); return; }
    setManualName("");
    applySize({ name, w, h });
  };

  const deleteManualSize = (name) => {
    const next = savedSizes.filter((x) => x.name !== name);
    setSavedSizes(next);
    try { localStorage.setItem(SAVED_PRINT_SIZES_KEY, JSON.stringify(next)); }
    catch (_) { alert("Could not update saved print sizes."); }
    if (activeSize === name) setActiveSize("");
  };

  const controlStyle = {
    width: "100%", boxSizing: "border-box", background: "#0f0c08",
    border: "1px solid #382f21", borderRadius: 5, color: "#f1ece1",
    padding: "8px", fontSize: 12, fontFamily: "inherit",
  };

  return (
    <section style={{ background: "#161009", border: "1px solid #2c2418", borderRadius: 8, padding: "0.9rem" }}>
      <Label>Print Item Size · A4 / A5 / A6</Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
        {PRINT_SIZE_PRESETS.map((size) => {
          const fit = calculatePrintFit(sheetW, sheetH, size.w, size.h);
          return (
            <button key={size.name} type="button" onClick={() => applySize(size)} style={{
              background: activeSize === size.name ? "#241c11" : "transparent",
              border: `1px solid ${activeSize === size.name ? "#c9962f" : "#382f21"}`,
              borderRadius: 5, color: "#e0ab45", padding: "8px 4px",
              cursor: "pointer", fontFamily: "inherit", fontSize: 12,
            }}>
              <strong>{size.name}</strong>
              <div style={{ fontSize: 10, color: "#a08f6e", marginTop: 3 }}>{size.w}×{size.h}</div>
              <div style={{ fontSize: 10, color: "#d8cdb4", marginTop: 2 }}>{Math.min(maxCount, fit)} fit</div>
            </button>
          );
        })}
      </div>
      <Hint>Fit count checks both normal and rotated item orientation on the current sheet. Selecting a size updates Images Per Page while preserving the existing grid layouts.</Hint>
      <Label>Manual Print Size (mm)</Label>
      <input value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Size name (e.g. 120×180)" style={{ ...controlStyle, marginBottom: 6 }} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <input type="number" min="0.1" step="0.1" value={manualW} onChange={(e) => setManualW(e.target.value)} placeholder="Width (mm)" style={controlStyle} />
        <input type="number" min="0.1" step="0.1" value={manualH} onChange={(e) => setManualH(e.target.value)} placeholder="Height (mm)" style={controlStyle} />
      </div>
      <button type="button" onClick={saveManualSize} style={{
        width: "100%", marginTop: 7, padding: "8px", background: "#241c11",
        border: "1px solid #c9962f", borderRadius: 5, color: "#e0ab45",
        cursor: "pointer", fontFamily: "inherit", fontSize: 12,
      }}>Save & Apply Manual Size</button>
      {savedSizes.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <Label>Saved Manual Sizes</Label>
          {savedSizes.map((size) => (
            <div key={size.name} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5, padding: 6, border: "1px solid #382f21", borderRadius: 5 }}>
              <button type="button" onClick={() => applySize(size)} style={{
                flex: 1, textAlign: "left", background: "transparent", border: 0,
                color: activeSize === size.name ? "#e0ab45" : "#d8cdb4",
                cursor: "pointer", fontFamily: "inherit", fontSize: 11,
              }}>{size.name} · {size.w} × {size.h} mm</button>
              <button type="button" onClick={() => deleteManualSize(size.name)} style={{
                background: "transparent", border: "1px solid #6b3528", borderRadius: 4,
                color: "#e07a5f", padding: "4px 7px", cursor: "pointer", fontSize: 10,
              }}>Delete</button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---- Row-split guide marks (dark blue, edges only) -------------------------
const ROW_SPLIT_MARK_COLOR = "#1e3a8a"; // dark blue
const ROW_SPLIT_MARK_RGB = [30, 58, 138];
const ROW_SPLIT_MARK_WIDTH_MM = 0.6; // stroke width used in the exported PDF
const EDGE_TICK_LENGTH_MM = 5;        // length of each short edge tick (not a full line)

// Fractional y-position (0-1) of the boundary between the top row of
// images and the bottom row of images, per images-per-page layout.
// null means the layout has only a single row (nothing "top vs bottom"
// to mark), so no horizontal tick is drawn for that layout.
const ROW_SPLIT_FRACTION = {
  1: null, // single full-bleed slot, no top/bottom split
  2: null, // side-by-side single row, no top/bottom split
  3: 0.55, // 1 image on top, 2 on bottom
  4: 0.5,
  5: 0.5,
  6: 0.5,
  7: 0.5,
  8: 0.5,
};

// ---- Fractional (0-1) layout templates per images-per-page ---------------
const LAYOUTS = {
  1: [{ x: 0, y: 0, w: 1, h: 1 }],

  2: [
    { x: 0,   y: 0, w: 0.5, h: 1 },
    { x: 0.5, y: 0, w: 0.5, h: 1 },
  ],

  3: [
    { x: 0,   y: 0,    w: 1,   h: 0.55 },
    { x: 0,   y: 0.55, w: 0.5, h: 0.45 },
    { x: 0.5, y: 0.55, w: 0.5, h: 0.45 },
  ],

  4: [
    { x: 0,   y: 0,   w: 0.5, h: 0.5 },
    { x: 0.5, y: 0,   w: 0.5, h: 0.5 },
    { x: 0,   y: 0.5, w: 0.5, h: 0.5 },
    { x: 0.5, y: 0.5, w: 0.5, h: 0.5 },
  ],

  // 2 photos on the first row + 3 photos on the second row.
  5: [
    { x: 0,     y: 0,   w: 0.5,   h: 0.5 },
    { x: 0.5,   y: 0,   w: 0.5,   h: 0.5 },
    { x: 0,     y: 0.5, w: 1 / 3, h: 0.5 },
    { x: 1 / 3, y: 0.5, w: 1 / 3, h: 0.5 },
    { x: 2 / 3, y: 0.5, w: 1 / 3, h: 0.5 },
  ],

  // 3 × 2 grid.
  6: [
    { x: 0,     y: 0,   w: 1 / 3, h: 0.5 },
    { x: 1 / 3, y: 0,   w: 1 / 3, h: 0.5 },
    { x: 2 / 3, y: 0,   w: 1 / 3, h: 0.5 },
    { x: 0,     y: 0.5, w: 1 / 3, h: 0.5 },
    { x: 1 / 3, y: 0.5, w: 1 / 3, h: 0.5 },
    { x: 2 / 3, y: 0.5, w: 1 / 3, h: 0.5 },
  ],

  // 4 photos on the first row + 3 photos on the second row.
  7: [
    { x: 0,     y: 0,   w: 0.25,  h: 0.5 },
    { x: 0.25,  y: 0,   w: 0.25,  h: 0.5 },
    { x: 0.5,   y: 0,   w: 0.25,  h: 0.5 },
    { x: 0.75,  y: 0,   w: 0.25,  h: 0.5 },
    { x: 0,     y: 0.5, w: 1 / 3, h: 0.5 },
    { x: 1 / 3, y: 0.5, w: 1 / 3, h: 0.5 },
    { x: 2 / 3, y: 0.5, w: 1 / 3, h: 0.5 },
  ],

  // 4 × 2 grid.
  8: [
    { x: 0,    y: 0,   w: 0.25, h: 0.5 },
    { x: 0.25, y: 0,   w: 0.25, h: 0.5 },
    { x: 0.5,  y: 0,   w: 0.25, h: 0.5 },
    { x: 0.75, y: 0,   w: 0.25, h: 0.5 },
    { x: 0,    y: 0.5, w: 0.25, h: 0.5 },
    { x: 0.25, y: 0.5, w: 0.25, h: 0.5 },
    { x: 0.5,  y: 0.5, w: 0.25, h: 0.5 },
    { x: 0.75, y: 0.5, w: 0.25, h: 0.5 },
  ],
};

// ---- File / image helpers --------------------------------------------------
const readFileAsDataURL = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = (e) => res(e.target.result);
    r.onerror = () => rej(new Error("Read failed"));
    r.readAsDataURL(file);
  });

const loadImageObj = (src) =>
  new Promise((res, rej) => {
    const img = new window.Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
    if (img.complete && img.naturalWidth) res(img);
  });

let jsPDFPromise = null;
function loadJsPDF() {
  if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve(window.jspdf.jsPDF);
  if (!jsPDFPromise) {
    jsPDFPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      s.onload = () => resolve(window.jspdf.jsPDF);
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return jsPDFPromise;
}

// Contain-fit box math with independent common / horizontal / vertical zoom.
// Base contain-fit keeps the complete image visible at 1x.
// zoom, zoomX and zoomY can enlarge the image to use more slot space.
// The slot clip prevents any enlarged image from overlapping neighbouring slots.
function computeSlotDraw(
  natW, natH,
  boxX, boxY, boxW, boxH,
  zoom, zoomX, zoomY,
  offX, offY
) {
  const containScale = Math.min(boxW / natW, boxH / natH);

  const dw = natW * containScale * zoom * zoomX;
  const dh = natH * containScale * zoom * zoomY;

  const maxPanX = Math.max(0, (dw - boxW) / 2);
  const maxPanY = Math.max(0, (dh - boxH) / 2);

  const baseX = boxX + (boxW - dw) / 2;
  const baseY = boxY + (boxH - dh) / 2;

  return {
    dw,
    dh,
    drawX: baseX + offX * maxPanX,
    drawY: baseY + offY * maxPanY,
    maxPanX,
    maxPanY,
  };
}

// Book-fold margin math.
// The binding/inner edge gets DOUBLE the normal outer page margin.
// Page 0 is treated as a right-hand page: inner edge = LEFT.
// Page 1 is a left-hand page: inner edge = RIGHT.
// This alternates for the whole book.
function slotRectPx(template, pagePxW, pagePxH, outerPx, gapPx, pageIndex = 0) {
  // FINAL SPACING RULE:
  // 1) Every OUTSIDE page border uses the same gap: outerPx.
  // 2) Every INTERNAL gap between neighbouring images is DOUBLE: outerPx * 2.
  //
  // This keeps left, right, top and bottom outside borders equal,
  // while both horizontal and vertical gaps between photos are twice as large.
  //
  // gapPx is kept in the function signature for compatibility with the
  // existing component/UI, but the final layout is derived from outerPx
  // so the required 1 : 2 relationship is always exact.

  const outsideGap = outerPx;
  const insideGap = outerPx * 2;

  const areaX0 = outsideGap;
  const areaY0 = outsideGap;
  const areaW = pagePxW - outsideGap * 2;
  const areaH = pagePxH - outsideGap * 2;

  // Exact fractional rectangle before internal-gap insets.
  const rawX = areaX0 + template.x * areaW;
  const rawY = areaY0 + template.y * areaH;
  const rawW = template.w * areaW;
  const rawH = template.h * areaH;

  // Each internal edge contributes half of insideGap.
  // Therefore two adjacent image edges produce:
  // insideGap / 2 + insideGap / 2 = insideGap = 2 × outsideGap.
  const EPS = 1e-9;
  const touchesLeft = template.edgeLeft ?? (template.x <= EPS);
  const touchesRight = template.edgeRight ?? (template.x + template.w >= 1 - EPS);
  const touchesTop = template.edgeTop ?? (template.y <= EPS);
  const touchesBottom = template.edgeBottom ?? (template.y + template.h >= 1 - EPS);

  const insetLeft = touchesLeft ? 0 : insideGap / 2;
  const insetRight = touchesRight ? 0 : insideGap / 2;
  const insetTop = touchesTop ? 0 : insideGap / 2;
  const insetBottom = touchesBottom ? 0 : insideGap / 2;

  return {
    x: rawX + insetLeft,
    y: rawY + insetTop,
    w: Math.max(0, rawW - insetLeft - insetRight),
    h: Math.max(0, rawH - insetTop - insetBottom),
  };
}


// Short blue cutting ticks at the midpoint of every shared grid seam.
function getGridCutTicks(rects, length = EDGE_TICK_LENGTH_MM) {
  const ticks = [], eps = 0.75, maxGap = 100;
  for (let i = 0; i < rects.length; i++) for (let j = i + 1; j < rects.length; j++) {
    const a = rects[i], b = rects[j];
    const oy = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
    const ox = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
    const cy = Math.max(a.y, b.y) + oy / 2, cx = Math.max(a.x, b.x) + ox / 2;
    const gapRight = b.x - (a.x + a.w), gapLeft = a.x - (b.x + b.w);
    const gapDown = b.y - (a.y + a.h), gapUp = a.y - (b.y + b.h);
    if (oy > eps && gapRight >= -eps && gapRight <= maxGap) {
      const x = (a.x + a.w + b.x) / 2;
      ticks.push({x1:x,y1:cy-length/2,x2:x,y2:cy+length/2});
    } else if (oy > eps && gapLeft >= -eps && gapLeft <= maxGap) {
      const x = (b.x + b.w + a.x) / 2;
      ticks.push({x1:x,y1:cy-length/2,x2:x,y2:cy+length/2});
    } else if (ox > eps && gapDown >= -eps && gapDown <= maxGap) {
      const y = (a.y + a.h + b.y) / 2;
      ticks.push({x1:cx-length/2,y1:y,x2:cx+length/2,y2:y});
    } else if (ox > eps && gapUp >= -eps && gapUp <= maxGap) {
      const y = (b.y + b.h + a.y) / 2;
      ticks.push({x1:cx-length/2,y1:y,x2:cx+length/2,y2:y});
    }
  }
  return ticks;
}
function drawGridCutTicksCanvas(ctx, rects, ppm) {
  ctx.save(); ctx.strokeStyle = ROW_SPLIT_MARK_COLOR; ctx.lineWidth = Math.max(1.25, 0.35 * ppm); ctx.setLineDash([]);
  getGridCutTicks(rects, EDGE_TICK_LENGTH_MM * ppm).forEach(t => { ctx.beginPath(); ctx.moveTo(t.x1,t.y1); ctx.lineTo(t.x2,t.y2); ctx.stroke(); });
  ctx.restore();
}
function drawGridCutTicksPdf(pdf, rects) {
  pdf.setDrawColor(...ROW_SPLIT_MARK_RGB); pdf.setLineWidth(ROW_SPLIT_MARK_WIDTH_MM); pdf.setLineDashPattern([],0);
  getGridCutTicks(rects).forEach(t => pdf.line(t.x1,t.y1,t.x2,t.y2));
}

// ---- Draw one full page onto a canvas -------------------------------------
function renderPage(ctx, pagePxW, pagePxH, layout, pageImages, outerPx, gapPx, pageIndex, selectedId, natSizeCache, showRowSplitMarks, ppm) {
  ctx.clearRect(0, 0, pagePxW, pagePxH);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, pagePxW, pagePxH);

  const template = layout;
  template.forEach((t, i) => {
    const rect = slotRectPx(t, pagePxW, pagePxH, outerPx, gapPx, pageIndex);
    const entry = pageImages[i];

    if (!entry) {
      ctx.fillStyle = "#f4f1ea";
      ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
      ctx.strokeStyle = "#d8d0bd";
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(rect.x + 1, rect.y + 1, rect.w - 2, rect.h - 2);
      ctx.setLineDash([]);
      ctx.fillStyle = "#a89f8a";
      ctx.font = `${Math.max(10, rect.w * 0.06)}px Georgia, serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("empty", rect.x + rect.w / 2, rect.y + rect.h / 2);
      return;
    }

    const nat = natSizeCache.get(entry.id);
    if (!nat) return;

    ctx.save();
    ctx.beginPath();
    ctx.rect(rect.x, rect.y, rect.w, rect.h);
    ctx.clip();
    const { dw, dh, drawX, drawY } = computeSlotDraw(
      nat.w, nat.h, rect.x, rect.y, rect.w, rect.h,
      entry.zoom, entry.zoomX, entry.zoomY,
      entry.offX, entry.offY
    );
    ctx.drawImage(nat.imgObj, drawX, drawY, dw, dh);
    ctx.restore();

    if (entry.id === selectedId) {
      ctx.strokeStyle = "#c9962f";
      ctx.lineWidth = Math.max(2, rect.w * 0.006);
      ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
    } else {
      ctx.strokeStyle = "rgba(20,16,10,0.15)";
      ctx.lineWidth = 1;
      ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
    }
  });

  // Tiny blue cutting ticks between adjacent grids only.
  if (showRowSplitMarks) {
    const rects = template.map(t => slotRectPx(t, pagePxW, pagePxH, outerPx, gapPx, pageIndex));
    drawGridCutTicksCanvas(ctx, rects, ppm);
  }
}

// ---- Small UI atoms ---------------------------------------------------------
function Label({ children }) {
  return (
    <label style={{
      display: "block", fontSize: 10.5, letterSpacing: "0.14em",
      color: "#a08f6e", marginBottom: 7, textTransform: "uppercase",
      fontFamily: "'Iowan Old Style','Georgia',serif",
    }}>{children}</label>
  );
}

function Hint({ children }) {
  return <p style={{ fontSize: 11, color: "#7a705c", marginTop: 5, lineHeight: 1.5 }}>{children}</p>;
}

function Select({ value, onChange, children }) {
  return (
    <select value={value} onChange={onChange} style={{
      width: "100%", background: "#1a1611", border: "1px solid #382f21",
      borderRadius: 6, color: "#f1ece1", padding: "9px 12px", fontSize: 13,
      cursor: "pointer", fontFamily: "'DM Mono','Fira Mono',monospace",
    }}>{children}</select>
  );
}

function ProgressBar({ value, total, label }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: "#a08f6e" }}>{label}</span>
        <span style={{ fontSize: 11, color: "#c9962f" }}>{value} / {total} ({pct}%)</span>
      </div>
      <div style={{ background: "#241e15", borderRadius: 4, height: 6, overflow: "hidden" }}>
        <div style={{
          width: `${pct}%`, height: "100%",
          background: "linear-gradient(90deg,#a8781e,#e0ab45)",
          transition: "width 0.2s", borderRadius: 4,
        }} />
      </div>
    </div>
  );
}

function Thumbnail({ src, index, selected, onSelect, onRemove }) {
  return (
    <div
      onClick={() => onSelect(index)}
      style={{
        position: "relative", width: 60, height: 60,
        border: `2px solid ${selected ? "#c9962f" : "#2c2418"}`,
        borderRadius: 5, overflow: "hidden", cursor: "pointer",
        flexShrink: 0, background: "#14110c",
      }}
    >
      <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(index); }}
        style={{
          position: "absolute", top: 1, right: 1,
          background: "rgba(0,0,0,0.75)", border: "none",
          borderRadius: 3, color: "#f18b6d", fontSize: 10, lineHeight: 1,
          padding: "1px 3px", cursor: "pointer", fontFamily: "inherit",
        }}
      >×</button>
      <div style={{
        position: "absolute", bottom: 1, left: 2,
        fontSize: 9, color: "rgba(255,255,255,0.55)", userSelect: "none",
      }}>{index + 1}</div>
    </div>
  );
}

const extensionButtonStyle = {
  width: 36,
  height: 32,
  background: "#1a1611",
  border: "1px solid #382f21",
  borderRadius: 5,
  color: "#e0ab45",
  fontFamily: "'DM Mono','Fira Mono',monospace",
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1,
};

// ============================================================================
// Main component
// ============================================================================
export default function BooksPdfComposer() {
  const [canvasMode, setCanvasMode] = useState("normal"); // Normal is the default
  const [extensionMM, setExtensionMM] = useState(0); // 0–20 mm along the selected axis
  const [extensionAxis, setExtensionAxis] = useState("vertical"); // vertical is the default
  const [pageKey, setPageKey]         = useState("landscape");
  const [perPage, setPerPage]         = useState(2);
  const [gapMM, setGapMM]             = useState(4);
  const [outerMM, setOuterMM]         = useState(8);
  const [showRowSplitMarks, setShowRowSplitMarks] = useState(true);
  const [fillAllGrids, setFillAllGrids] = useState(false);
  const [applyAdjustmentsToAll, setApplyAdjustmentsToAll] = useState(false);

  const [images, setImages]           = useState([]); // {id,name,dataUrl,zoom,zoomX,zoomY,offX,offY}
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedId, setSelectedId]   = useState(null);

  const [isDragging, setIsDragging]   = useState(false); // file drag-over
  const [generating, setGenerating]   = useState(false);
  const [progress, setProgress]       = useState({ done: 0, total: 0 });

  const canvasRef    = useRef(null);
  const fileInputRef = useRef(null);
  const natCache     = useRef(new Map()); // id -> { imgObj, w, h }
  const panState     = useRef(null);      // active drag info

  const basePageW = pageKey === "landscape" ? SHEET_SIZE.w : SHEET_SIZE.h;
  const basePageH = pageKey === "landscape" ? SHEET_SIZE.h : SHEET_SIZE.w;

  // All selected photo counts use equal physical cells. Build a regular grid,
  // then compensate each raw rectangle for slotRectPx's half-gap insets so the
  // final visible rectangles remain exactly equal at edges and in the middle.
  const activeExtensionMM = canvasMode === "extended" ? extensionMM : 0;
  const pageW = basePageW + ((extensionAxis === "horizontal" || extensionAxis === "both") ? activeExtensionMM : 0);
  const pageH = basePageH + ((extensionAxis === "vertical" || extensionAxis === "both") ? activeExtensionMM : 0);

  const activeLayout = useMemo(() => {
    const count = Number(perPage);
    const usableW = Math.max(1, pageW - outerMM * 2);
    const usableH = Math.max(1, pageH - outerMM * 2);
    const insideGap = outerMM * 2;
    // Pick a sensible row/column count for the page orientation.
    let best = { cols: 1, rows: count, score: Infinity };
    for (let cols = 1; cols <= count; cols++) {
      const rows = Math.ceil(count / cols);
      const cellW = (usableW - insideGap * (cols - 1)) / cols;
      const cellH = (usableH - insideGap * (rows - 1)) / rows;
      if (cellW <= 0 || cellH <= 0) continue;
      const score = Math.abs(Math.log(cellW / cellH));
      if (score < best.score) best = { cols, rows, score };
    }
    const { cols, rows } = best;
    const cellW = Math.max(1, (usableW - insideGap * (cols - 1)) / cols);
    const cellH = Math.max(1, (usableH - insideGap * (rows - 1)) / rows);
    return Array.from({ length: count }, (_, i) => {
      const r = Math.floor(i / cols);
      const c = i % cols;
      const leftInset = c > 0 ? insideGap / 2 : 0;
      const hasNextInRow = i + 1 < count && Math.floor((i + 1) / cols) === r;
      const rightInset = hasNextInRow ? insideGap / 2 : 0;
      const topInset = r > 0 ? insideGap / 2 : 0;
      const hasNextRow = r < rows - 1;
      const bottomInset = hasNextRow ? insideGap / 2 : 0;
      return {
        x: (c * (cellW + insideGap) - leftInset) / usableW,
        y: (r * (cellH + insideGap) - topInset) / usableH,
        w: (cellW + leftInset + rightInset) / usableW,
        h: (cellH + topInset + bottomInset) / usableH,
        edgeLeft: c === 0,
        edgeRight: !hasNextInRow,
        edgeTop: r === 0,
        edgeBottom: !hasNextRow,
      };
    });
  }, [perPage, pageW, pageH, outerMM]);

  const totalPages = Math.max(1, Math.ceil(images.length / activeLayout.length) || 1);
  const gridSlotSizes = useMemo(() => activeLayout.map((template) => {
    const rect = slotRectPx(template, pageW, pageH, outerMM, gapMM, currentPage);
    return { width: rect.w, height: rect.h };
  }), [activeLayout, pageW, pageH, outerMM, gapMM, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages - 1) setCurrentPage(Math.max(0, totalPages - 1));
  }, [totalPages, currentPage]);

  const pages = useMemo(() => {
    const chunks = [];
    for (let i = 0; i < images.length; i += activeLayout.length) chunks.push(images.slice(i, i + activeLayout.length));
    if (chunks.length === 0) chunks.push([]);
    return chunks;
  }, [images, activeLayout.length]);

  const currentPageImages = pages[currentPage] || [];

  // ---- Load natural sizes for any new images --------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const img of images) {
        if (!natCache.current.has(img.id)) {
          try {
            const obj = await loadImageObj(img.dataUrl);
            if (cancelled) return;
            natCache.current.set(img.id, { imgObj: obj, w: obj.naturalWidth, h: obj.naturalHeight });
            setImages((prev) => [...prev]); // trigger repaint
          } catch (_) { /* ignore broken image */ }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [images]);

  // ---- Preview canvas ---------------------------------------------------------
  const PPM = 3.2;
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pxW = Math.round(pageW * PPM);
    const pxH = Math.round(pageH * PPM);
    canvas.width = pxW;
    canvas.height = pxH;
    const ctx = canvas.getContext("2d");
    renderPage(
      ctx, pxW, pxH, activeLayout, currentPageImages,
      outerMM * PPM, gapMM * PPM, currentPage, selectedId, natCache.current,
      showRowSplitMarks, PPM
    );
  }, [pageW, pageH, activeLayout, currentPageImages, outerMM, gapMM, selectedId, images, showRowSplitMarks]);

  // ---- Upload handling ---------------------------------------------------------
  const addFiles = useCallback(async (files) => {
    const imgFiles = Array.from(files).filter((f) => f.type.startsWith("image/")).slice(0, 500);
    const entries = await Promise.all(imgFiles.map(async (f, idx) => ({
      id: `img_${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 7)}`,
      name: f.name,
      dataUrl: await readFileAsDataURL(f),
      zoom: 1, zoomX: 1, zoomY: 1, offX: 0, offY: 0,
    })));
    setImages((prev) => {
      const incoming = fillAllGrids && entries.length === 1
        ? Array.from({ length: activeLayout.length }, (_, i) => ({ ...entries[0], id: `${entries[0].id}_grid_${i + 1}` }))
        : entries;
      return [...prev, ...incoming].slice(0, 2000);
    });
  }, [fillAllGrids, activeLayout.length]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  // ---- Selection + drag-to-pan on the preview canvas ---------------------------
  const findSlotAtPoint = useCallback((px, py) => {
    const pxW = Math.round(pageW * PPM);
    const pxH = Math.round(pageH * PPM);
    const template = activeLayout;
    for (let i = 0; i < template.length; i++) {
      const rect = slotRectPx(template[i], pxW, pxH, outerMM * PPM, gapMM * PPM, currentPage);
      if (px >= rect.x && px <= rect.x + rect.w && py >= rect.y && py <= rect.y + rect.h) {
        return { index: i, rect };
      }
    }
    return null;
  }, [pageW, pageH, activeLayout, outerMM, gapMM, currentPage]);

  const canvasPointFromEvent = (e) => {
    const canvas = canvasRef.current;
    const bounds = canvas.getBoundingClientRect();
    const scaleX = canvas.width / bounds.width;
    const scaleY = canvas.height / bounds.height;
    return {
      x: (e.clientX - bounds.left) * scaleX,
      y: (e.clientY - bounds.top) * scaleY,
    };
  };

  const onCanvasMouseDown = (e) => {
    const pt = canvasPointFromEvent(e);
    const hit = findSlotAtPoint(pt.x, pt.y);
    if (!hit) return;
    const entry = currentPageImages[hit.index];
    if (!entry) return;
    setSelectedId(entry.id);
    const nat = natCache.current.get(entry.id);
    if (!nat) return;
    const { maxPanX, maxPanY } = computeSlotDraw(
      nat.w, nat.h, hit.rect.x, hit.rect.y, hit.rect.w, hit.rect.h,
      entry.zoom, entry.zoomX, entry.zoomY,
      entry.offX, entry.offY
    );
    panState.current = {
      id: entry.id, startX: pt.x, startY: pt.y,
      startOffX: entry.offX, startOffY: entry.offY,
      maxPanX, maxPanY,
    };
  };

  const onCanvasMouseMove = (e) => {
    if (!panState.current) return;
    const pt = canvasPointFromEvent(e);
    const { id, startX, startY, startOffX, startOffY, maxPanX, maxPanY } = panState.current;
    const dx = pt.x - startX;
    const dy = pt.y - startY;
    const nOffX = maxPanX > 0 ? clamp(startOffX + dx / maxPanX, -1, 1) : 0;
    const nOffY = maxPanY > 0 ? clamp(startOffY + dy / maxPanY, -1, 1) : 0;
    updateImage(id, { offX: nOffX, offY: nOffY });
  };

  const endPan = () => { panState.current = null; };

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  const updateImage = (id, patch) => {
    setImages((prev) => prev.map((img) => (img.id === id ? { ...img, ...patch } : img)));
  };

  // Zoom buttons use physical millimetres, not arbitrary 0.01× increments.
  // Common zoom changes rendered width by 0.5 mm and preserves aspect ratio;
  // horizontal/vertical zoom change only the selected dimension by 0.5 mm.
  const adjustZoom = (id, key, direction) => {
    const image = images.find((item) => item.id === id);
    const nat = natCache.current.get(id);
    const slotIndex = currentPageImages.findIndex((item) => item.id === id);
    if (!image || !nat || slotIndex < 0) return;

    const template = activeLayout[slotIndex];
    const rect = slotRectPx(template, pageW, pageH, outerMM, gapMM, currentPage);
    const containScale = Math.min(rect.w / nat.w, rect.h / nat.h);
    const baseW = nat.w * containScale;
    const baseH = nat.h * containScale;
    if (!(baseW > 0 && baseH > 0)) return;

    const currentW = baseW * image.zoom * image.zoomX;
    const currentH = baseH * image.zoom * image.zoomY;
    const next = { ...image };

    if (key === "zoom") {
      const targetW = Math.max(0.1, currentW + direction * ZOOM_STEP_MM);
      next.zoom = clamp(targetW / (baseW * image.zoomX), MIN_ZOOM, MAX_ZOOM);
    } else if (key === "zoomX") {
      const targetW = Math.max(0.1, currentW + direction * ZOOM_STEP_MM);
      next.zoomX = clamp(targetW / (baseW * image.zoom), MIN_ZOOM, MAX_ZOOM);
    } else if (key === "zoomY") {
      const targetH = Math.max(0.1, currentH + direction * ZOOM_STEP_MM);
      next.zoomY = clamp(targetH / (baseH * image.zoom), MIN_ZOOM, MAX_ZOOM);
    }

    setImages((prev) => prev.map((item) => {
      if (item.id === id) return next;
      return applyAdjustmentsToAll ? { ...item, [key]: next[key] } : item;
    }));
  };

  const selectedImage = images.find((i) => i.id === selectedId) || null;
  const selectedDrawSize = useMemo(() => {
    if (!selectedImage) return null;
    const nat = natCache.current.get(selectedImage.id);
    const slotIndex = currentPageImages.findIndex((item) => item.id === selectedImage.id);
    if (!nat || slotIndex < 0) return null;
    const rect = slotRectPx(activeLayout[slotIndex], pageW, pageH, outerMM, gapMM, currentPage);
    const scale = Math.min(rect.w / nat.w, rect.h / nat.h);
    return {
      width: nat.w * scale * selectedImage.zoom * selectedImage.zoomX,
      height: nat.h * scale * selectedImage.zoom * selectedImage.zoomY,
    };
  }, [selectedImage, currentPageImages, activeLayout, pageW, pageH, outerMM, gapMM, currentPage, images]);

  // ---- PDF generation -----------------------------------------------------------
  // Images are written directly into jsPDF. The preview canvas/background,
  // empty-slot placeholders and selection borders are NOT exported.
  // The solid blue center guide line IS baked into the PDF, but only when
  // showRowSplitMarks is checked — otherwise they are skipped entirely.
  const handleGenerate = async () => {
    if (images.length === 0) return;

    setGenerating(true);
    setProgress({ done: 0, total: pages.length });
    await new Promise((r) => setTimeout(r, 30));

    try {
      const jsPDF = await loadJsPDF();
      const orientation = pageW > pageH ? "l" : "p";
      const pdf = new jsPDF({
        orientation,
        unit: "mm",
        format: [pageW, pageH],
        compress: true,
      });

      for (let p = 0; p < pages.length; p++) {
        if (p > 0) {
          pdf.addPage([pageW, pageH], orientation);
        }

        const pageImages = pages[p];
        const template = activeLayout;

        for (let i = 0; i < template.length; i++) {
          const entry = pageImages[i];
          if (!entry) continue;

          const nat = natCache.current.get(entry.id);
          if (!nat) continue;

          const rect = slotRectPx(
            template[i],
            pageW,
            pageH,
            outerMM,
            gapMM,
            p
          );

          const draw = computeSlotDraw(
            nat.w,
            nat.h,
            rect.x,
            rect.y,
            rect.w,
            rect.h,
            entry.zoom,
            entry.zoomX,
            entry.zoomY,
            entry.offX,
            entry.offY
          );

          // Direct PDF images are clipped to the slot when zoom makes them
          // larger than their allocated grid area. The clipping path
          // itself must never be stroked/drawn into the PDF.
          pdf.saveGraphicsState();

          // IMPORTANT:
          // Build the clipping rectangle as a path ONLY.
          // Passing null prevents jsPDF from stroking/drawing the rectangle
          // into the final PDF.
          pdf.rect(rect.x, rect.y, rect.w, rect.h, null);
          pdf.clip();
          pdf.discardPath();

          let format = "JPEG";
          if (entry.dataUrl.startsWith("data:image/png")) format = "PNG";
          else if (entry.dataUrl.startsWith("data:image/webp")) format = "WEBP";

          pdf.addImage(
            entry.dataUrl,
            format,
            draw.drawX,
            draw.drawY,
            draw.dw,
            draw.dh,
            undefined,
            "FAST"
          );

          pdf.restoreGraphicsState();
        }

        // Same tiny blue seam ticks as the preview; never draw full grid lines.
        if (showRowSplitMarks) {
          const rects = template.map(t => slotRectPx(t, pageW, pageH, outerMM, gapMM, p));
          drawGridCutTicksPdf(pdf, rects);
        }

        setProgress({ done: p + 1, total: pages.length });
        await new Promise((r) => setTimeout(r, 0));
      }

      pdf.save(`book_${pages.length}pages_${canvasMode}${activeExtensionMM ? `_${activeExtensionMM.toFixed(1)}mm` : ""}_${pageKey}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Failed to generate PDF: " + err.message);
    } finally {
      setGenerating(false);
      setProgress({ done: 0, total: 0 });
    }
  };

  const mmToIn = (mm) => (mm / 25.4).toFixed(1);
  const canGen = images.length > 0 && !generating;

  // ---- Render -------------------------------------------------------------------
  return (
    <div style={{
      minHeight: "100vh", background: "#0e0b07",
      fontFamily: "'DM Mono','Fira Mono',monospace",
      color: "#f1ece1", display: "flex", flexDirection: "column",
    }}>
      <header style={{
        borderBottom: "1px solid #241e15", padding: "1.25rem 2rem",
        display: "flex", alignItems: "baseline", gap: "1rem",
      }}>
        <span style={{
          fontSize: 11, letterSpacing: "0.2em", color: "#a08f6e",
          textTransform: "uppercase",
        }}>/books</span>
        <h1 style={{
          fontSize: 21, fontWeight: 400, margin: 0, color: "#f6efe1",
          letterSpacing: "-0.01em", fontFamily: "'Iowan Old Style','Georgia',serif",
        }}>
          Pagebind — Photo Book Composer
        </h1>
      </header>

      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "360px 1fr", overflow: "hidden" }}>
        {/* ---------------- Sidebar ---------------- */}
        <aside style={{
          borderRight: "1px solid #241e15", padding: "1.5rem",
          display: "flex", flexDirection: "column", gap: "1.25rem", overflowY: "auto",
        }}>
          {/* Fixed sheet size */}
          <section>
            <Label>Sheet Size</Label>
            <div style={{ marginTop: 8, padding: "12px", background: "#21190c", border: "1px solid #c9962f", borderRadius: 8 }}>
              <div style={{ fontWeight: 700, color: "#e0ab45", marginBottom: 4 }}>{SHEET_SIZE.label}</div>
              <div style={{ fontSize: 12, color: "#d8cdb4" }}>Sheet: {pageW} × {pageH} mm</div>
              <div style={{ fontSize: 11, color: "#a08f6e", marginTop: 5 }}>Equal-size photo grid · {activeLayout.length} photo{activeLayout.length === 1 ? "" : "s"} per sheet</div>
              <div style={{ fontSize: 11, color: "#a08f6e", marginTop: 3 }}>Outside margin: {outerMM.toFixed(1)} mm · Internal gaps: {(outerMM * 2).toFixed(1)} mm</div>
            </div>
          </section>

          <PrintSizeControls
            sheetW={pageW}
            sheetH={pageH}
            perPage={perPage}
            setPerPage={setPerPage}
            maxCount={100}
          />

          {/* Canvas size mode */}
          <section style={{ background: "#161009", border: "1px solid #2c2418", borderRadius: 8, padding: "0.9rem" }}>
            <Label>Canvas Mode</Label>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[{ value: "normal", label: "Normal" }, { value: "extended", label: "Extended" }].map((option) => (
                <label key={option.value} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14, fontWeight: 600, color: "#f1ece1" }}>
                  <input
                    type="radio"
                    name="canvasMode"
                    value={option.value}
                    checked={canvasMode === option.value}
                    onChange={() => setCanvasMode(option.value)}
                    style={{ width: 17, height: 17, accentColor: "#c9962f" }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            {canvasMode === "extended" ? (
              <div style={{ marginTop: 12, padding: 10, border: "1px solid #c9962f", borderRadius: 8, background: "#21190c" }}>
                <Label>Extension Direction</Label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 6, marginBottom: 12 }}>
                  {[
                    { value: "vertical", label: "Vertical", detail: "Height +" },
                    { value: "horizontal", label: "Horizontal", detail: "Width +" },
                    { value: "both", label: "Both", detail: "Width + height +" },
                  ].map((option) => (
                    <label key={option.value} style={{ display: "flex", alignItems: "flex-start", gap: 5, padding: 7, minWidth: 0, border: `1px solid ${extensionAxis === option.value ? "#c9962f" : "#382f21"}`, borderRadius: 6, background: extensionAxis === option.value ? "#2b210f" : "#120e09", cursor: "pointer" }}>
                      <input type="radio" name="extensionAxis" value={option.value} checked={extensionAxis === option.value} onChange={() => setExtensionAxis(option.value)} style={{ marginTop: 2, accentColor: "#c9962f", flexShrink: 0 }} />
                      <span><strong style={{ display: "block", fontSize: 11, color: "#f1ece1" }}>{option.label}</strong><span style={{ display: "block", fontSize: 9, color: "#a08f6e", marginTop: 3, lineHeight: 1.3 }}>{option.detail}</span></span>
                    </label>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 11, color: "#e0ab45", textTransform: "uppercase", letterSpacing: "0.08em" }}>{extensionAxis === "vertical" ? "Height extension" : extensionAxis === "horizontal" ? "Width extension" : "Width + height extension"}</span>
                  <strong style={{ color: "#f1ece1", fontSize: 13 }}>{extensionMM.toFixed(1)} mm</strong>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "36px 1fr 36px", gap: 7, alignItems: "center" }}>
                  <button type="button" aria-label="Decrease canvas extension" disabled={extensionMM <= 0} onClick={() => setExtensionMM((v) => Math.max(0, Math.round((v - CANVAS_EXTENSION_STEP_MM) * 10) / 10))} style={{ ...extensionButtonStyle, opacity: extensionMM <= 0 ? 0.4 : 1, cursor: extensionMM <= 0 ? "not-allowed" : "pointer" }}>−</button>
                  <input type="range" min="0" max={MAX_CANVAS_EXTENSION_MM} step={CANVAS_EXTENSION_STEP_MM} value={extensionMM} onChange={(e) => setExtensionMM(Number(e.target.value))} aria-label={`${extensionAxis} canvas extension in millimetres`} style={{ width: "100%", accentColor: "#c9962f" }} />
                  <button type="button" aria-label="Increase canvas extension" disabled={extensionMM >= MAX_CANVAS_EXTENSION_MM} onClick={() => setExtensionMM((v) => Math.min(MAX_CANVAS_EXTENSION_MM, Math.round((v + CANVAS_EXTENSION_STEP_MM) * 10) / 10))} style={{ ...extensionButtonStyle, opacity: extensionMM >= MAX_CANVAS_EXTENSION_MM ? 0.4 : 1, cursor: extensionMM >= MAX_CANVAS_EXTENSION_MM ? "not-allowed" : "pointer" }}>+</button>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#a08f6e", marginTop: 4 }}><span>0 mm</span><span>20 mm max</span></div>
                <Hint>Each +/− click changes the extension by 0.5 mm. {extensionAxis === "vertical" ? "Only height" : extensionAxis === "horizontal" ? "Only width" : "Both width and height by the same amount"}; grid slots, preview and exported PDF update automatically.</Hint>
              </div>
            ) : (
              <Hint>Normal keeps the standard sheet dimensions. Select Extended to choose vertical (height), horizontal (width), or Both (width and height) extension from 0 to 20 mm.</Hint>
            )}
            <div style={{ fontSize: 11, color: "#d8cdb4", marginTop: 8, lineHeight: 1.6 }}>
              <div>Standard: {basePageW} × {basePageH} mm</div>
              <div>Current canvas: {pageW} × {pageH} mm</div>
            </div>
          </section>
          <section>
            <Label>Sheet Orientation</Label>
            <Select value={pageKey} onChange={(e) => setPageKey(e.target.value)}>
              <option value="landscape">Landscape</option>
              <option value="portrait">Portrait</option>
            </Select>
            <Hint>{pageW.toFixed(1)} × {pageH.toFixed(1)} mm ({mmToIn(pageW)} × {mmToIn(pageH)} in)</Hint>
          </section>

          {/* Images per page */}
          <section>
            <Label>Images Per Page</Label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <button
                  key={n}
                  onClick={() => setPerPage(n)}
                  style={{
                    width: "100%", background: perPage === n ? "#241c11" : "transparent",
                    border: `1px solid ${perPage === n ? "#c9962f" : "#2c2418"}`,
                    borderRadius: 6, color: perPage === n ? "#e0ab45" : "#a08f6e",
                    padding: "10px 0", fontSize: 14, cursor: "pointer",
                    fontFamily: "inherit", fontWeight: 600, transition: "all 0.15s",
                  }}
                >{n}</button>
              ))}
            </div>
            <Hint>{pages.length} page{pages.length !== 1 ? "s" : ""} total for {images.length} image{images.length !== 1 ? "s" : ""}.</Hint>
            <div style={{ marginTop: 12, padding: 10, border: "1px solid #2c2418", borderRadius: 8, background: "#120e09" }}>
              <div style={{ fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a08f6e", marginBottom: 8 }}>Grid & Slot Sizes</div>
              <GridLayoutIndicator layout={activeLayout} pageW={pageW} pageH={pageH} />
              <div style={{ fontSize: 11, color: "#d8cdb4", marginTop: 8, lineHeight: 1.6 }}>
                <div><strong>{activeLayout.length} photo{activeLayout.length === 1 ? "" : "s"} per sheet</strong> · {pageW} × {pageH} mm</div>
                {gridSlotSizes.map((size, index) => (
                  <div key={index}>Photo {index + 1}: {size.width.toFixed(1)} × {size.height.toFixed(1)} mm</div>
                ))}
              </div>
              <Hint>All photo slots have equal physical width and height. Outside margins stay equal and each internal gap is twice the outside margin. Preview, size readout, and exported PDF use the same grid calculations.</Hint>
            </div>
          </section>

          {/* Row-split guide marks */}
          <section style={{
            background: "#161009", border: "1px solid #2c2418", borderRadius: 8, padding: "0.9rem",
          }}>
            <label style={{
              display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
            }}>
              <input
                type="checkbox"
                checked={showRowSplitMarks}
                onChange={(e) => setShowRowSplitMarks(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: ROW_SPLIT_MARK_COLOR, cursor: "pointer" }}
              />
              <span style={{
                fontSize: 10.5, letterSpacing: "0.14em", color: "#a08f6e",
                textTransform: "uppercase", fontFamily: "'Iowan Old Style','Georgia',serif",
              }}>
                Blue Grid Cutting Marks (Tiny Ticks)
              </span>
              <span style={{
                marginLeft: "auto", width: 22, height: 4, borderRadius: 2,
                background: ROW_SPLIT_MARK_COLOR, flexShrink: 0,
              }} />
            </label>
            <Hint>
              Two short dark blue horizontal ticks (5mm each — not a full line), one at the left edge and one at
              the right edge of the printable area, sitting exactly where the top row of images meets the bottom
              row (layouts with only one row skip the marks). Shown in the preview and,
              when checked, baked into the exported PDF. Uncheck to leave the PDF page clean.
            </Hint>
          </section>

          {/* Margins */}
          <section>
            <Label>Photo Spacing</Label>
            <Hint>
              Outside border: {outerMM.toFixed(1)} mm · Horizontal gap: {(outerMM * 2).toFixed(1)} mm · Vertical gap: {(outerMM * 2).toFixed(1)} mm
            </Hint>
          </section>
          <section>
            <Label>Outer Page Margin (mm)</Label>
            <input
              type="range" min="0" max="30" step="0.5" value={outerMM}
              onChange={(e) => setOuterMM(parseFloat(e.target.value))}
              style={{ width: "100%", accentColor: "#c9962f" }}
            />
            <Hint>
              All outside borders: {outerMM.toFixed(1)} mm · Internal horizontal & vertical gaps: {(outerMM * 2).toFixed(1)} mm.
            </Hint>
          </section>

          {/* Selected image adjust */}
          <section style={{
            background: "#161009", border: "1px solid #2c2418", borderRadius: 8, padding: "0.9rem",
          }}>
            <p style={{
              fontSize: 10.5, color: "#a08f6e", margin: "0 0 8px", textTransform: "uppercase",
              letterSpacing: "0.12em", fontFamily: "'Iowan Old Style','Georgia',serif",
            }}>Adjust Selected Photo</p>

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer", fontSize: 11, color: "#d8cdb4" }}>
              <input type="checkbox" checked={applyAdjustmentsToAll} onChange={e => setApplyAdjustmentsToAll(e.target.checked)} />
              Apply width / height adjustments to all images
            </label>
            {selectedImage ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <img src={selectedImage.dataUrl} alt="" style={{
                    width: 40, height: 40, objectFit: "cover", borderRadius: 4,
                    border: "1px solid #382f21",
                  }} />
                  <span style={{ fontSize: 11, color: "#d8cdb4", wordBreak: "break-all" }}>
                    {selectedImage.name}
                  </span>
                </div>

                <ZoomControl
                  label="Common Zoom"
                  readout={selectedDrawSize ? `${selectedDrawSize.width.toFixed(1)} × ${selectedDrawSize.height.toFixed(1)} mm` : "Loading dimensions…"}
                  onMinus={() => adjustZoom(selectedImage.id, "zoom", -1)}
                  onPlus={() => adjustZoom(selectedImage.id, "zoom", 1)}
                />
                <Hint>Each click changes rendered width by 0.5 mm; height scales proportionally to preserve the image ratio.</Hint>

                <ZoomControl
                  label="Horizontal Zoom"
                  readout={selectedDrawSize ? `Width: ${selectedDrawSize.width.toFixed(1)} mm` : "Loading dimensions…"}
                  onMinus={() => adjustZoom(selectedImage.id, "zoomX", -1)}
                  onPlus={() => adjustZoom(selectedImage.id, "zoomX", 1)}
                />
                <Hint>Changes only rendered width by 0.5 mm per click.</Hint>

                <ZoomControl
                  label="Vertical Zoom"
                  readout={selectedDrawSize ? `Height: ${selectedDrawSize.height.toFixed(1)} mm` : "Loading dimensions…"}
                  onMinus={() => adjustZoom(selectedImage.id, "zoomY", -1)}
                  onPlus={() => adjustZoom(selectedImage.id, "zoomY", 1)}
                />
                <Hint>Changes only rendered height by 0.5 mm per click.</Hint>

                <button
                  onClick={() => updateImage(selectedImage.id, {
                    zoom: 1,
                    zoomX: 1,
                    zoomY: 1,
                    offX: 0,
                    offY: 0,
                  })}
                  style={{
                    marginTop: 8, width: "100%", background: "transparent",
                    border: "1px solid #382f21", borderRadius: 5,
                    color: "#a08f6e", padding: "7px 10px", fontSize: 11,
                    cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  Reset zoom & position
                </button>

                <Hint>
                  Use + / − to make the selected image use more or less slot space.
                  Enlarged images stay clipped to their own slot and cannot overlap neighbouring images.
                  Drag the image on the preview to reposition cropped areas.
                </Hint>
              </>
            ) : (
              <Hint>
                Click a photo on the page preview or its thumbnail to select it,
                then use Common, Horizontal or Vertical zoom.
              </Hint>
            )}
          </section>

          {/* Upload */}
          <section>
            <Label>Upload Images</Label>
            <div
              onClick={() => fileInputRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              style={{
                border: `1.5px dashed ${isDragging ? "#c9962f" : images.length ? "#4a7a4f" : "#382f21"}`,
                borderRadius: 8, padding: "1.1rem", textAlign: "center", cursor: "pointer",
                transition: "all 0.2s", background: isDragging ? "#1c1408" : "transparent",
              }}
            >
              {images.length > 0 ? (
                <>
                  <p style={{ fontSize: 12, color: "#7fb583", margin: "0 0 4px" }}>
                    {images.length} image{images.length > 1 ? "s" : ""} loaded
                  </p>
                  <p style={{ fontSize: 11, color: "#7a705c", margin: 0 }}>Drop more or click to add</p>
                </>
              ) : (
                <>
                  <p style={{ fontSize: 22, margin: "0 0 8px", color: "#382f21" }}>↑</p>
                  <p style={{ fontSize: 13, color: "#7a705c", margin: 0 }}>
                    Drop images or click to browse
                  </p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef} type="file" accept="image/*" multiple
              onChange={(e) => addFiles(e.target.files)}
              style={{ display: "none" }}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, cursor: "pointer", fontSize: 11, color: "#d8cdb4" }}>
              <input type="checkbox" checked={fillAllGrids} onChange={e => setFillAllGrids(e.target.checked)} />
              Fill all grids with one uploaded image
            </label>

            {images.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{
                  display: "flex", gap: 6, flexWrap: "wrap",
                  maxHeight: 140, overflowY: "auto", padding: "4px 2px",
                }}>
                  {images.map((img, i) => (
                    <Thumbnail
                      key={img.id} src={img.dataUrl} index={i}
                      selected={img.id === selectedId}
                      onSelect={() => {
                        setSelectedId(img.id);
                        setCurrentPage(Math.floor(i / activeLayout.length));
                      }}
                      onRemove={(idx) => {
                        setImages((prev) => prev.filter((_, j) => j !== idx));
                        if (images[idx].id === selectedId) setSelectedId(null);
                      }}
                    />
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                  <p style={{ fontSize: 11, color: "#7a705c", margin: 0 }}>
                    {images.length} image{images.length !== 1 ? "s" : ""} · {pages.length} page{pages.length !== 1 ? "s" : ""}
                  </p>
                  <button
                    onClick={() => { setImages([]); setSelectedId(null); natCache.current.clear(); }}
                    style={{
                      background: "transparent", border: "none", color: "#e07a5f",
                      fontSize: 11, cursor: "pointer", fontFamily: "inherit", padding: 0,
                    }}
                  >Clear all</button>
                </div>
              </div>
            )}

            {generating && progress.total > 0 && (
              <ProgressBar value={progress.done} total={progress.total} label="Rendering pages…" />
            )}
          </section>

          {/* Generate */}
          <button
            onClick={handleGenerate}
            disabled={!canGen}
            style={{
              background: canGen ? "#c9962f" : "#241e15",
              border: "none", borderRadius: 8,
              color: canGen ? "#150f08" : "#4a4030",
              padding: "13px", fontSize: 14, fontWeight: 700,
              cursor: canGen ? "pointer" : "not-allowed",
              fontFamily: "inherit", letterSpacing: "0.04em", transition: "all 0.2s",
            }}
          >
            {generating
              ? `Generating… (${progress.done}/${progress.total})`
              : images.length === 0
              ? "Upload images first"
              : `Download PDF (${pages.length} page${pages.length !== 1 ? "s" : ""})`}
          </button>
        </aside>

        {/* ---------------- Preview ---------------- */}
        <main style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "2rem", background: "#080603", overflow: "auto",
        }}>
          <div style={{ textAlign: "center" }}>
            <p style={{
              fontSize: 11, color: "#4a4030", marginBottom: "0.9rem",
              letterSpacing: "0.12em", textTransform: "uppercase",
            }}>
              Page {currentPage + 1} of {pages.length} — {pageW.toFixed(0)} × {pageH.toFixed(0)} mm
            </p>

            {pages.length > 1 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 12 }}>
                <NavBtn onClick={() => setCurrentPage((p) => Math.max(0, p - 1))} disabled={currentPage === 0}>◀ Prev</NavBtn>
                <span style={{ fontSize: 12, color: "#7a705c", lineHeight: "30px" }}>
                  {currentPage + 1} / {pages.length}
                </span>
                <NavBtn onClick={() => setCurrentPage((p) => Math.min(pages.length - 1, p + 1))} disabled={currentPage === pages.length - 1}>Next ▶</NavBtn>
              </div>
            )}

            <div style={{
              boxShadow: "0 0 0 1px #241e15, 0 24px 70px rgba(0,0,0,0.85)",
              display: "inline-block", borderRadius: 3, overflow: "hidden",
            }}>
              <canvas
                ref={canvasRef}
                onMouseDown={onCanvasMouseDown}
                onMouseMove={onCanvasMouseMove}
                onMouseUp={endPan}
                onMouseLeave={endPan}
                style={{
                  display: "block", cursor: currentPageImages.length ? "grab" : "default",
                  maxWidth: "min(760px, 100%)", maxHeight: "72vh",
                  width: "auto", height: "auto",
                }}
              />
            </div>

            {images.length === 0 ? (
              <p style={{ fontSize: 12, color: "#382f21", marginTop: "1rem" }}>
                Upload photos to start composing your book
              </p>
            ) : (
              <p style={{ fontSize: 11, color: "#4a4030", marginTop: "0.9rem" }}>
                Select a photo · use + / − Common, Horizontal or Vertical zoom · drag to reposition
              </p>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}


function GridLayoutIndicator({ layout, pageW, pageH }) {
  return (
    <div style={{
      position: "relative", width: "100%", aspectRatio: `${pageW} / ${pageH}`,
      background: "#080603", border: "1px solid #6f5a31", borderRadius: 4,
      overflow: "hidden",
    }}>
      {layout.map((slot, index) => (
        <div key={index} style={{
          position: "absolute",
          left: `${slot.x * 100}%`, top: `${slot.y * 100}%`,
          width: `${slot.w * 100}%`, height: `${slot.h * 100}%`,
          boxSizing: "border-box", border: "1px solid #c9962f",
          background: "rgba(201,150,47,0.12)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#e0ab45", fontSize: 10, fontWeight: 700,
        }}>{index + 1}</div>
      ))}
    </div>
  );
}

function ZoomControl({ label, readout, onMinus, onPlus }) {
  const btnStyle = {
    width: 36,
    height: 32,
    background: "#1a1611",
    border: "1px solid #382f21",
    borderRadius: 5,
    color: "#e0ab45",
    cursor: "pointer",
    fontFamily: "'DM Mono','Fira Mono',monospace",
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1,
  };

  return (
    <div style={{ marginTop: 10 }}>
      <Label>{label}</Label>
      <div style={{
        display: "grid",
        gridTemplateColumns: "36px 1fr 36px",
        gap: 7,
        alignItems: "center",
      }}>
        <button type="button" onClick={onMinus} style={btnStyle}>−</button>
        <div style={{
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid #2c2418",
          borderRadius: 5,
          background: "#0f0c08",
          color: "#d8cdb4",
          fontSize: 12,
        }}>
          {readout}
        </div>
        <button type="button" onClick={onPlus} style={btnStyle}>+</button>
      </div>
    </div>
  );
}

function NavBtn({ onClick, disabled, children }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: disabled ? "transparent" : "#1a1611",
      border: `1px solid ${disabled ? "#241e15" : "#382f21"}`,
      borderRadius: 5, color: disabled ? "#241e15" : "#c4b697",
      padding: "4px 10px", fontSize: 11, cursor: disabled ? "default" : "pointer",
      fontFamily: "'DM Mono','Fira Mono',monospace", transition: "all 0.15s",
    }}>{children}</button>
  );
}
